import { useEffect, useEffectEvent, useState } from 'react'

import { useNavigate, useSearch } from '@tanstack/react-router'

import { usePostHog } from '@posthog/react'
import { useDebounce } from '@uidotdev/usehooks'

/**
 * Options for useTableSearch hook
 */
export type UseTableSearchOptions = {
	/** URL param name for search query (default: 'q') */
	paramName?: string
	/** Debounce delay in ms (default: 300) */
	debounceMs?: number
	/**
	 * Fewest non-space characters that count as a search (default: 0). A
	 * shorter query is no search: the URL param is removed.
	 */
	minLength?: number
	/** PostHog analytics event name */
	analyticsEvent?: string
}

/**
 * Return type for useTableSearch hook
 */
export type UseTableSearchReturn = {
	/** Current search query value */
	query: string
	/** Update search query */
	setQuery: (query: string) => void
	/**
	 * Apply a query now, without waiting for the debounce: the current one on
	 * Enter, or `''` to clear.
	 */
	submit: (value?: string) => void
	/** Debounced query value (for API calls); `''` below `minLength` */
	debouncedQuery: string
}

/**
 * Hook for managing table search state with URL sync
 *
 * Features:
 * - Syncs search query with URL params
 * - Debounces input to prevent excessive URL updates; `submit` skips the wait
 * - Optional minimum length, below which the query is no search
 * - Optional PostHog analytics tracking
 * - Reads initial value from URL on mount
 *
 * @example
 * const { query, setQuery, debouncedQuery } = useTableSearch({
 *   analyticsEvent: 'playbooks_search',
 * })
 *
 * // Pass debouncedQuery to useDataTable for filtering
 * const { columns, options } = useDataTable({
 *   globalFilter: debouncedQuery,
 *   ...
 * })
 */
export function useTableSearch(
	options: UseTableSearchOptions = {},
): UseTableSearchReturn {
	const {
		paramName = 'q',
		debounceMs = 300,
		minLength = 0,
		analyticsEvent,
	} = options

	const navigate = useNavigate()
	const posthog = usePostHog()

	// Get current search params (strict: false allows use in any route)
	const searchParams = useSearch({ strict: false }) as Record<
		string,
		string | undefined
	>
	const urlQuery = searchParams[paramName] ?? ''

	// Local state for immediate UI updates
	const [query, setQuery] = useState(urlQuery)

	// Track previous URL value for sync
	const [prevUrlQuery, setPrevUrlQuery] = useState(urlQuery)

	// Sync from URL -> local state when URL changes externally
	if (urlQuery !== prevUrlQuery) {
		setPrevUrlQuery(urlQuery)
		setQuery(urlQuery)
	}

	const debounced = useDebounce(query, debounceMs)

	// A query applied on `submit`, ahead of the debounce. It stands until the
	// debounce catches up with the input, so typing on after Enter keeps it
	// rather than falling back to an older debounced value.
	const [submitted, setSubmitted] = useState<string | null>(null)
	if (submitted !== null && debounced === query) {
		setSubmitted(null)
	}

	const submit = (value: string = query) => {
		setQuery(value)
		setSubmitted(value)
	}

	// The query as last applied: after the debounce, or at once on `submit`.
	const applied = submitted ?? debounced

	// Debounced value for URL updates and API calls
	const debouncedQuery = applied.trim().length >= minLength ? applied : ''

	// Stable function for URL update (avoids useEffect dependencies)
	const updateURL = useEffectEvent(() => {
		navigate({
			to: '.',
			search: (prev) => ({
				...prev,
				[paramName]: debouncedQuery || undefined,
			}),
		})
	})

	// Sync from local state -> URL when debounced value changes
	useEffect(() => {
		// Track search analytics (only for non-empty searches)
		if (analyticsEvent && debouncedQuery !== '') {
			posthog.capture(analyticsEvent, {
				query: debouncedQuery,
			})
		}

		updateURL()
	}, [debouncedQuery, analyticsEvent, posthog])

	return {
		query,
		setQuery,
		submit,
		debouncedQuery,
	}
}
